import { OrderLineViewModel } from '@/@types/client-api';
import classnames from 'classnames';
import Link from '../../Link';
import QuantityInput from './QuantityInput';
import Icon from '../../Icon';
import { MyCartLabels } from '../labels';
import { formatPrice } from '@/utils/helpers';
import { trackRemoveFromCartEvent, trackSelectItem } from '@/analytics/ecommerce';
import { AnalyticsContext } from '@/analytics/constants';
import useCatalogLabels from '@/hooks/PDP/useCatalogLabels';

export interface CartItemProps {
  item: OrderLineViewModel;
  index?: number;
  isPageContext: boolean;
  md: boolean;
  selectedQuantities: Record<string, string>;
  labels: ReturnType<typeof MyCartLabels>;
  context?: 'panel' | 'page';
  handleQuantityChange: (
    item: OrderLineViewModel,
    newQuantity: string,
    shouldUpdateCartItem: boolean
  ) => void;
  handleDeleteCartItem: (item: OrderLineViewModel) => void;
  handleRemovePromoCode: (item: OrderLineViewModel) => void;
}

const CartItem = ({
  item,
  index,
  isPageContext,
  md,
  selectedQuantities,
  labels,
  context,
  handleQuantityChange,
  handleDeleteCartItem,
  handleRemovePromoCode
}: CartItemProps) => {
  const { otherLabels } = useCatalogLabels();
  const isPreorderItem = item.publicationDate && new Date(item.publicationDate) > new Date();

  const handleProductClick = () => {
    const analyticsContext =
      context === 'panel' ? AnalyticsContext.CartPanel : AnalyticsContext.CartPage;
    const listName = context === 'panel' ? 'CartPanel' : 'CartPage';
    const listId = context === 'panel' ? 'CartPanel01' : 'CartPage01';

    trackSelectItem(
      item.itemAnalyticsModel,
      analyticsContext,
      listId,
      listName,
      item.itemListAnalyticsModel,
      typeof index === 'number' && index >= 0 ? index : undefined
    );
  };

  const trackAndDeleteItem = (item: OrderLineViewModel) => {
    const analyticsContext =
      context === 'panel' ? AnalyticsContext.CartPanel : AnalyticsContext.CartPage;
    trackRemoveFromCartEvent(item, analyticsContext);
    handleDeleteCartItem(item);
  };

  const trackAndQuantityChange = (
    item: OrderLineViewModel,
    newQuantity: string,
    shouldUpdateCartItem: boolean
  ) => {
    if (newQuantity && parseInt(newQuantity) === 0) {
      const analyticsContext =
        context === 'panel' ? AnalyticsContext.CartPanel : AnalyticsContext.CartPage;
      trackRemoveFromCartEvent(item, analyticsContext);
    }

    handleQuantityChange(item, newQuantity, shouldUpdateCartItem);
  };

  const StatusMessages = () => {
    const baseStatusMessageClasses = classnames('text-2-fixed rounded-[4px] p-3');
    const grayStatusMessageClasses = classnames(
      baseStatusMessageClasses,
      'bg-gray-feather text-gray-dark'
    );
    const orangeStatusMessageClasses = classnames(
      baseStatusMessageClasses,
      'bg-orange/10 text-orange'
    );

    return (
      <>
        <div
          id={`quantity-error-backorder-${item.code!}`}
          className={classnames(orangeStatusMessageClasses, {
            hidden: !item.isBackorder || isPreorderItem
          })}
        >
          {labels.outOfStockText}
        </div>
        <div
          id={`preorder-item-${item.code!}`}
          className={classnames(grayStatusMessageClasses, {
            hidden: !isPreorderItem
          })}
        >
          {labels.preOrderText}
        </div>
        <div
          id={`poec-live-item-${item.code!}`}
          className={classnames(grayStatusMessageClasses, {
            hidden: !item.isLiveProgramPBRWebcast
          })}
        >
          {labels.POECLiveWebcastText}
        </div>
      </>
    );
  };

  const PromoCode = () =>
    (item.otherDiscountValue ?? 0) > 0 &&
    !!item?.priorityCode?.length && (
      <div className="flex gap-2 text-gray-dark">
        <div className="flex items-center gap-1">
          <Icon name="check" size="small" />
          <span className="label">{`${item.priorityCode} promo applied`}</span>
        </div>
        <Link
          as="button"
          onClick={() => handleRemovePromoCode(item)}
          href="#"
          text="Remove"
          variant="text-link-2"
          className="text-gray-dark"
        />
      </div>
    );

  const BulkDiscount = () =>
    item.isStockItem &&
    otherLabels.bulkDiscountQuantity &&
    +selectedQuantities[item.code!] >= otherLabels.bulkDiscountQuantity && (
      <div className="flex gap-2 text-gray-dark">
        <div className="flex items-center gap-1">
          <Icon name="check" size="small" />
          <span className="label">{`Bulk discount`}</span>
        </div>
      </div>
    );

  return (
    <div
      className="flex max-w-full flex-col gap-4 border-b border-solid border-gray-light py-6"
      aria-describedby={`quantity-error-backorder-${item.code!} preorder-item-${item.code!} poec-live-item-${item.code!}`}
    >
      <div className="flex flex-col gap-2">
        <div className="text-2 text-gray-dark">{item.displayType}</div>
        <div className="flex">
          <div className="text-1 w-[70%]">
            <Link
              href={item.productVariantUrl ?? ''}
              text={item.displayName ?? ''}
              title={item.displayName ?? ''}
              variant="text-link-1"
              className="no-underline"
              onClick={handleProductClick}
            />
          </div>
          <div className="w-[10%]"></div>
          <div className="text-1 flex w-1/5 flex-col items-end text-right">
            <div
              className={`flex items-end gap-1 ${isPageContext && md ? 'flex-row' : 'flex-col-reverse'}`}
            >
              <span
                className={classnames('font-[400] text-gray line-through', {
                  hidden: item.extendedPrice === item.discountPrice
                })}
              >
                {formatPrice(item.extendedPrice ?? 0)}
              </span>
              <span className="pl-2 font-[500]">{formatPrice(item.discountPrice ?? 0)}</span>
            </div>
          </div>
        </div>
        <PromoCode />
        <BulkDiscount />
      </div>

      <StatusMessages />

      <div className="text-1 flex items-center justify-between">
        <div>
          {item.isStockItem ? (
            <QuantityInput
              item={item}
              labels={labels}
              currentQuantity={selectedQuantities[item.code!]}
              onQuantityChange={trackAndQuantityChange}
            />
          ) : (
            item.quantity
          )}
        </div>
        <button onClick={() => trackAndDeleteItem(item)}>
          <Icon name="delete" size="large" className="text-link cursor-pointer" />
        </button>
      </div>
    </div>
  );
};

export default CartItem;
